<template>
    <div id="manage-stock-count">
        <!-- Action Buttons -->
        <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
                <button class="btn btn-alt-warning" @click="setAllToCountedQuantity()">
                    จัดการตามนับได้
                </button>
                <button class="btn btn-alt-success" @click="exportExcel()">
                    Export Excel
                </button>
            </div>
            <div class="d-flex align-items-center gap-3">
                <div class="input-group" style="width: 300px;">
                    <input type="text" class="form-control" placeholder="ค้นหาสินค้า..." v-model="searchQuery"
                        @keyup.enter="searchProducts">
                    <button class="btn btn-primary" type="button" @click="searchProducts">
                        <i class="fas fa-search"></i>
                    </button>
                    <button v-if="searchQuery" class="btn btn-secondary" type="button" @click="clearSearch">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
                <span>จำนวนที่จัดการ {{ managedCount }}/{{ pagination.total }}</span>
            </div>
        </div>

        <!-- Filters Component -->
        <stock-count-filters v-model:brand-id="filterBrandId" v-model:category-id="filterCategoryId" :brands="brands"
            :categories="categories" @brand-select="onBrandSelect" @brand-deselect="onBrandDeselect"
            @category-select="onCategorySelect" @category-deselect="onCategoryDeselect" />

        <!-- Pagination Info -->
        <div v-if="pagination.total > 0" class="mb-2 text-muted">
            แสดง {{ pagination.from }} - {{ pagination.to }} จาก {{ pagination.total }} รายการ
        </div>

        <!-- Product Table Component -->
        <stock-count-product-table :products="stock_count_products" :actions="stock_count_actions"
            :sort-field="sortField" :sort-order="sortOrder" @toggle-sort="toggleSort" @manage-lot="manageLot" />

        <!-- Pagination Component -->
        <stock-count-pagination v-if="pagination.total_pages > 1" :pagination="pagination" :per-page="perPage"
            @change-page="goToPage" @change-per-page="changePerPage" />

        <!-- Edit Lot Modal Component -->
        <stock-count-edit-lot-modal ref="editLotModal" :modal-item="modalItem" :selected-lots="selected_lots"
            :stock-count-actions="stock_count_actions" @confirm="confirmLot" @add-new-lot="addNewLot"
            :stock-count-lot-actions="stock_count_lot_actions"
            @remove-lot="removeLot" @select-product-action="selectedProductAction"
            @select-lot-action="selectedLotAction" @view-lot-transactions="viewLotTransactions" />

        <!-- Lot Transactions Modal Component -->
        <stock-count-lot-transactions-modal ref="lotTransactionsModal" :current-lot-info="currentLotInfo"
            :lot-transactions="lotTransactions" :new-transaction-rows="newTransactionRows"
            :loading="lotTransactionsLoading" :target-quantity="parseInt(currentLotTargetQuantity)"
            :warehouses="warehouses" @add-transaction="addNewTransactionRow"
            @remove-transaction="removeNewTransactionRow" @validate-remaining="validateRemainingAmount"
            @confirm="confirmLotTransactions" />
    </div>
</template>

<script>
import StockCountFilters from './StockCount/StockCountFilters.vue';
import StockCountProductTable from './StockCount/StockCountProductTable.vue';
import StockCountPagination from './StockCount/StockCountPagination.vue';
import StockCountEditLotModal from './StockCount/StockCountEditLotModal.vue';
import StockCountLotTransactionsModal from './StockCount/StockCountLotTransactionsModal.vue';
import Swal from '/assets/js/plugins/sweetalert2/sweetalert2.min.js';
import '/assets/js/plugins/sweetalert2/sweetalert2.min.css';
export default {
    name: 'StockCountManage',

    components: {
        StockCountFilters,
        StockCountProductTable,
        StockCountPagination,
        StockCountEditLotModal,
        StockCountLotTransactionsModal,
    },

    props: {
        stockCountId: {
            type: String,
            required: true,
        },
        initialBrands: {
            type: Array,
            default: () => [],
        },
        initialCategories: {
            type: Array,
            default: () => [],
        },
        warehouses: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            stock_count_products: [],
            stock_count_lots: [],
            stock_count_actions: [],
            stock_count_lot_actions: [],
            selected_product_id: null,
            modalItem: null,
            pagination: {
                current_page: 1,
                per_page: 50,
                total: 0,
                total_pages: 0,
                from: 0,
                to: 0,
            },
            perPage: 50,
            searchQuery: '',
            filterBrandId: null,
            filterCategoryId: null,
            brands: [],
            categories: [],
            sortField: null,
            sortOrder: null,
            allManagedProducts: {},
            allManagedLots: {},
            lotTransactions: [],
            lotTransactionsSummary: null,
            lotTransactionsLoading: false,
            newTransactionRows: [],
            currentLotInfo: null,
            currentLotTargetQuantity: 0,
            distributeDebounceTimer: null,
        };
    },

    computed: {
        selected_lots() {
            return this.stock_count_lots.filter(
                (item) => item.product_id === this.selected_product_id
            );
        },

        managedCount() {
            return this.stock_count_products.filter(
                (item) => item.selected_product_action_id && item.latest_manage_at
            ).length;
        },

        totalRemainingAmount() {
            const existingTotal = this.lotTransactions.reduce(
                (sum, t) => sum + Number(t.remaining_amount || 0),
                0
            );
            const newTotal = this.newTransactionRows.reduce(
                (sum, t) => sum + Number(t.remaining_amount || 0),
                0
            );
            return existingTotal + newTotal;
        },

        totalChangeAmount() {
            const existingTotal = this.lotTransactions.reduce(
                (sum, t) => sum + Number(t.change_amount || 0),
                0
            );
            const newTotal = this.newTransactionRows.reduce(
                (sum, t) => sum + Number(t.change_amount || 0),
                0
            );
            return existingTotal + newTotal;
        },

        currentVsTarget() {
            const current = this.totalRemainingAmount;
            const target = this.getTargetQtyForCurrentLot();
            const diff = current - target;

            return {
                current: current,
                target: target,
                diff: diff,
                status: diff === 0 ? 'complete' : diff < 0 ? 'shortage' : 'excess',
            };
        },
    },

    mounted() {
        this.initializeData();
        this.loadStockCountProducts();
    },


    methods: {
        initializeData() {
            this.brands = [
                { id: 'null', text: 'ทั้งหมด' },
                ...this.initialBrands.map((brand) => ({
                    id: brand.id,
                    text: brand.name,
                })),
            ];

            this.categories = [
                { id: 'null', text: 'ทั้งหมด' },
                ...this.initialCategories.map((category) => ({
                    id: category.id,
                    text: category.name,
                })),
            ];
        },

        async loadStockCountProducts(page = 1) {
            window.loading_on();
            try {
                const params = {
                    page: page,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    brand_id: this.filterBrandId,
                    category_id: this.filterCategoryId,
                    sort_field: this.sortField,
                    sort_order: this.sortOrder,
                };

                const res = await axios.get(
                    `/stock-count/${this.stockCountId}/get-manage-products`,
                    { params }
                );

                this.stock_count_products = res.data.stock_count_products;
                this.stock_count_lots = res.data.stock_count_lots;
                this.stock_count_actions = res.data.stock_count_actions.filter(
                    (action) => action.id != 6
                );
                this.stock_count_lot_actions = res.data.stock_count_actions;

                if (res.data.pagination) {
                    this.pagination = res.data.pagination;
                }

                this.restoreManagedData();
            } catch (error) {
                console.error('Error loading products:', error);
                mySwal.fire({
                    title: 'เกิดข้อผิดพลาด',
                    text: 'ไม่สามารถโหลดข้อมูลได้',
                    icon: 'error',
                });
            } finally {
                window.loading_off();
            }
        },

        restoreManagedData() {
            this.stock_count_products.forEach((product) => {
                if (this.allManagedProducts[product.product_id]) {
                    Object.assign(product, this.allManagedProducts[product.product_id]);
                }
            });

            this.stock_count_lots.forEach((lot) => {
                const lotKey = `${lot.product_id}_${lot.manufacturing_no || 'new'}_${lot.expiry_date || 'temp'
                    }`;
                if (this.allManagedLots[lotKey]) {
                    Object.assign(lot, this.allManagedLots[lotKey]);
                }
            });
        },

        saveManagedData() {
            this.stock_count_products.forEach((product) => {
                if (product.is_edit || product.selected_product_action_id) {
                    this.allManagedProducts[product.product_id] = { ...product };
                }
            });

            this.stock_count_lots.forEach((lot) => {
                if (lot.is_edit || lot.selected_product_action_id) {
                    const lotKey = `${lot.product_id}_${lot.manufacturing_no || 'new'}_${lot.expiry_date || 'temp'
                        }`;
                    this.allManagedLots[lotKey] = {
                        ...lot,
                        pending_transactions: lot.pending_transactions || [],
                        has_pending_transactions: lot.has_pending_transactions || false,
                    };
                }
            });
        },

        // Search & Filter Methods
        searchProducts() {
            this.saveManagedData();
            this.loadStockCountProducts(1);
        },

        clearSearch() {
            this.searchQuery = '';
            this.saveManagedData();
            this.loadStockCountProducts(1);
        },

        onBrandSelect(node) {
            this.filterBrandId = node.id;
            this.applyFilters();
        },

        onBrandDeselect() {
            this.filterBrandId = '';
            this.applyFilters();
        },

        onCategorySelect(node) {
            this.filterCategoryId = node.id;
            this.applyFilters();
        },

        onCategoryDeselect() {
            this.filterCategoryId = '';
            this.applyFilters();
        },

        applyFilters() {
            this.saveManagedData();
            this.loadStockCountProducts(1);
        },

        // Pagination Methods
        goToPage(page) {
            const pageNum = Number(page);
            if (
                pageNum < 1 ||
                pageNum > this.pagination.total_pages ||
                pageNum === this.pagination.current_page
            ) {
                return;
            }

            this.saveManagedData();
            this.loadStockCountProducts(pageNum);
        },

        changePerPage(newPerPage) {
            this.perPage = newPerPage;
            this.saveManagedData();
            this.loadStockCountProducts(1);
        },

        // Sorting Methods
        toggleSort(field) {
            if (this.sortField === field) {
                if (this.sortOrder === 'asc') {
                    this.sortOrder = 'desc';
                } else if (this.sortOrder === 'desc') {
                    this.sortField = null;
                    this.sortOrder = null;
                }
            } else {
                this.sortField = field;
                this.sortOrder = 'asc';
            }

            this.saveManagedData();
            this.loadStockCountProducts(1);
        },

        // Lot Management Methods
        manageLot(index) {
            this.$refs.editLotModal.show();
            this.modalItem = this.stock_count_products[index];
            this.modalItem.index = index;
            this.selected_product_id = this.stock_count_products[index].product_id;

            // ตั้งค่า default สำหรับ product เป็น "นับได้ - ขายออก" (id = 5) หากยังไม่ได้เลือก
            const DEFAULT_PRODUCT_ACTION_ID = 5; // COUNTING_SOLD
            const DEFAULT_PRODUCT_ACTION_LABEL = 'ตั้งค่าตามนับได้ - ขายออก';

            // ตั้งค่า default สำหรับ lot เป็น "ตามคงเหลือ" (id = 6) หากยังไม่ได้เลือก
            const DEFAULT_LOT_ACTION_ID = 6; // REMAINING_AMOUNT
            const DEFAULT_LOT_ACTION_LABEL = 'ตามคงเหลือ';

            if (!this.modalItem.selected_product_action_id) {
                this.modalItem.selected_product_action_id = DEFAULT_PRODUCT_ACTION_ID;
                this.modalItem.selected_product_action_name = DEFAULT_PRODUCT_ACTION_LABEL;
            }

            // ตั้งค่า default สำหรับทุก lot ที่ยังไม่ได้เลือก
            this.selected_lots.forEach(lot => {
                if (!lot.selected_product_action_id) {
                    lot.selected_product_action_id = DEFAULT_LOT_ACTION_ID;
                    lot.selected_product_action_name = DEFAULT_LOT_ACTION_LABEL;
                }
            });
        },

        addNewLot() {
            if (!this.selected_product_id) return;

            const newLot = {
                product_id: this.selected_product_id,
                manufacturing_no: '',
                expiry_date: '',
                remaining_amount: 0,
                imported_amount: 0,
                expected_qty: 0,
                counted_qty: 0,
                sold_qty_before_count: 0,
                sold_qty_after_count: 0,
                selected_product_action_id: null,
                selected_product_action_name: null,
                action_manual: 0,
                is_new_lot: true,
            };

            this.stock_count_lots.push(newLot);
        },

        removeLot(index) {
            const lot = this.selected_lots[index];
            const lotIndex = this.stock_count_lots.findIndex(
                (lotItem) =>
                    lotItem.product_id === this.selected_product_id &&
                    lotItem.is_new_lot &&
                    lotItem === lot
            );

            if (lotIndex !== -1) {
                this.stock_count_lots.splice(lotIndex, 1);
            }
        },

        selectedProductAction(index, value) {
            this.stock_count_products[index].selected_product_action_id = value?.id;
            this.stock_count_products[index].selected_product_action_name = value?.label;
            this.stock_count_products[index].is_edit = true;

            if (value == null) {
                this.stock_count_products[index].action_manual = null;
            }

            this.saveManagedData();
            this.$forceUpdate();
        },

        selectedLotAction(index, value) {
            this.selected_lots[index].selected_product_action_id = value?.id;
            this.selected_lots[index].selected_product_action_name = value?.label;
            this.selected_lots[index].is_edit = true;

            this.saveManagedData();
            this.$forceUpdate();
        },

        async confirmLot() {
            // Validation logic here (same as original)
            const newLots = this.selected_lots.filter((lot) => lot.is_new_lot);
            for (let newLot of newLots) {
                if (!newLot.manufacturing_no || !newLot.expiry_date) {
                    mySwal.fire({
                        title: 'ข้อมูลไม่ครบถ้วน',
                        text: 'กรุณากรอกเลขล็อตและวันหมดอายุสำหรับล็อตใหม่',
                        icon: 'error',
                    });
                    return;
                }

                if (!newLot.selected_product_action_id) {
                    mySwal.fire({
                        title: 'ข้อมูลไม่ครบถ้วน',
                        text: 'กรุณาเลือกการจัดการสำหรับล็อตใหม่',
                        icon: 'error',
                    });
                    return;
                }
            }

            const product = this.stock_count_products.find(
                (item) => item.product_id === this.selected_product_id
            );

            if (!product.selected_product_action_id) {
                mySwal.fire({
                    title: 'ไม่สามารถบันทึกได้',
                    text: 'กรุณาเลือกการจัดการสำหรับสินค้า',
                    icon: 'error',
                });
                return;
            }

            const sumLot = this.getSumLotQty(this.selected_lots);
            const sumProduct = this.getProductQty(product);

            if (sumProduct !== sumLot) {
                mySwal.fire({
                    title: 'ไม่สามารถบันทึกได้',
                    html: `<p>จำนวนล็อตรวมกับจำนวนสินค้าไม่ตรงกัน</p>
                        <span>สินค้า ${sumProduct} | ล็อตรวม ${sumLot || 0}</span>`,
                    icon: 'error',
                });
                return;
            }

            product.is_edit = true;

            const lotsData = this.selected_lots.map((lot) => ({
                manufacturing_no: lot.manufacturing_no,
                expiry_date: lot.expiry_date,
                counted_qty: lot.counted_qty || 0,
                sold_qty_before_count: lot.sold_qty_before_count || 0,
                sold_qty_after_count: lot.sold_qty_after_count || 0,
                action: lot.selected_product_action_id,
                action_value: lot.action_manual || null,
                note: lot.note || null,
                is_new: lot.is_new_lot || false,
            }));

            const res = await axios.post('/stock-count/update-product-discrepancy', {
                id: product.id,
                product_id: product.product_id,
                product_action: product.selected_product_action_id,
                product_action_value: product.action_manual || null,
                lots: lotsData,
            });

            if (res.data.success) {
                product.latest_manage_at = res.data.timestamp;
                product.latest_manage_by = res.data.updated_by;
            }

            this.saveManagedData();
            this.$refs.editLotModal.hide();
        },

        // Transaction Methods
        async viewLotTransactions(lot) {
            this.$refs.lotTransactionsModal.show();
            this.lotTransactionsLoading = true;
            this.lotTransactions = [];
            this.newTransactionRows = [];
            this.currentLotInfo = lot;

            // Calculate target quantity for this lot
            this.currentLotTargetQuantity = this.getTargetQtyForCurrentLot();

            try {
                const response = await axios.get('/stock-count/get-lot-transactions', {
                    params: {
                        stock_count_id: this.stockCountId,
                        product_id: lot.product_id,
                        manufacturing_no: lot.manufacturing_no,
                        expiry_date: lot.expiry_date,
                    },
                });

                if (response.data.success) {
                    this.lotTransactions = response.data.transactions;
                    this.lotTransactionsSummary = response.data.summary;

                    // แยก discrepancy_details เป็น existing edits และ new rows
                    const existingEdits = [];
                    const newRows = [];

                    if (response.data.discrepancy_details && response.data.discrepancy_details.length > 0) {
                        response.data.discrepancy_details.forEach(detail => {
                            if (detail.stock_transaction_id) {
                                // เป็นการแก้ไข existing transaction
                                existingEdits.push({
                                    id: detail.stock_transaction_id,
                                    remaining_amount: detail.amount,
                                    shelf_level_id: detail.shelf_level_id || null,
                                });
                            } else {
                                // เป็น new transaction
                                newRows.push({
                                    code: detail.code || 'MANUAL-' + Date.now(),
                                    stock_transaction_id: null,
                                    change_amount: detail.amount,
                                    remaining_amount: detail.amount,
                                    prev_remaining_amount: detail.prev_remaining_amount || 0,
                                    description: detail.description || '',
                                    manufacturing_no: detail.manufacturing_no || this.currentLotInfo.manufacturing_no,
                                    expiry_date: detail.expiry_date || this.currentLotInfo.expiry_date,
                                    shelf_level_id: detail.shelf_level_id || null,
                                    is_new: true,
                                    is_manual: detail.is_manual || false,
                                    is_excess: detail.is_excess || false,
                                });
                            }
                        });

                        // อัพเดท remaining_amount ของ existing transactions
                        existingEdits.forEach(edit => {
                            const existingTx = this.lotTransactions.find(t => t.id === edit.id);
                            if (existingTx) {
                                existingTx.remaining_amount = edit.remaining_amount;
                                if (edit.shelf_level_id) {
                                    existingTx.shelf_level_id = edit.shelf_level_id;
                                }
                            }
                        });
                    }

                    this.newTransactionRows = newRows;
                }
            } catch (error) {
                console.error('Error loading lot transactions:', error);
                mySwal.fire({
                    title: 'เกิดข้อผิดพลาด',
                    text: 'ไม่สามารถดึงข้อมูล Stock Transactions ได้',
                    icon: 'error',
                });
                this.$refs.lotTransactionsModal.hide();
            } finally {
                this.lotTransactionsLoading = false;
            }
        },

        addNewTransactionRow() {
            if (!this.currentLotInfo) return;

            const newTransaction = {
                code: 'MANUAL-' + Date.now(),
                stock_transaction_id: null,
                remaining_amount: 0,
                change_amount: 0,
                description: '',
                manufacturing_no: this.currentLotInfo.manufacturing_no || '',
                expiry_date: this.currentLotInfo.expiry_date || '',
                shelf_level_id: null,
                is_new: true,
                is_manual: true,
            };

            this.newTransactionRows.push(newTransaction);
        },

        removeNewTransactionRow(index) {
            this.newTransactionRows.splice(index, 1);
        },

        validateRemainingAmount(transaction) {
            const maxAmount = Number(transaction.change_amount) || 0;

            if (Number(transaction.remaining_amount) > maxAmount) {
                transaction.remaining_amount = maxAmount;
            }

            if (Number(transaction.remaining_amount) < 0) {
                transaction.remaining_amount = 0;
            }

            this.$forceUpdate();
        },

        async confirmLotTransactions() {
            if (!this.currentLotInfo) return;

            const allTransactions = [];
            console.log(this.lotTransactions);

            this.lotTransactions.forEach((t) => {
                allTransactions.push({
                    stock_transaction_id: t.id,
                    remaining_amount: t.remaining_amount,
                    prev_remaining_amount: t.prev_remaining_amount || 0,
                    change_amount: t.change_amount,
                    description: t.description || '',
                    shelf_level_id: t.shelf_level_id || null,
                });
            });

            this.newTransactionRows.forEach((t) => {
                allTransactions.push({
                    stock_transaction_id: t.stock_transaction_id || null,
                    remaining_amount: t.remaining_amount,
                    prev_remaining_amount: 0,
                    change_amount: t.change_amount,
                    description: t.description || '',
                    shelf_level_id: t.shelf_level_id || null,
                    is_new: t.is_new || false,
                    is_manual: t.is_manual || false,
                });
            });

            try {
                window.loading_on();
                const response = await axios.post('/stock-count/save-lot-transactions', {
                    stock_count_id: this.stockCountId,
                    product_id: this.currentLotInfo.product_id,
                    manufacturing_no: this.currentLotInfo.manufacturing_no,
                    expiry_date: this.currentLotInfo.expiry_date,
                    transactions: allTransactions,
                });

                if (response.data.success) {
                    mySwal.fire({
                        title: 'สำเร็จ',
                        text: 'บันทึกการจัดการเรียบร้อย',
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false,
                    });

                    this.$refs.lotTransactionsModal.hide();
                }
            } catch (error) {
                console.error('Error saving lot transactions:', error);
                mySwal.fire({
                    title: 'เกิดข้อผิดพลาด',
                    text: 'ไม่สามารถบันทึกข้อมูลได้',
                    icon: 'error',
                });
            } finally {
                window.loading_off();
            }
        },

        // Utility Methods
        async setAllToCountedQuantity() {
            // แสดง Confirmation Dialog ก่อนทำการบันทึก
            const result = await Swal.fire({
                title: 'ยืนยันการจัดการตามนับได้',
                html: `
                    <div style="text-align: left;">
                        <p><strong>⚠️ คำเตือน:</strong></p>
                        <ul style="margin-left: 20px;">
                            <li>การกดยืนยันจะตั้งค่าการจัดการเป็น <strong>"ตามที่นับได้"</strong> สำหรับสินค้าทั้งหมด ${this.stock_count_products.length} รายการ</li>
                            <li>สินค้าที่ได้จัดการไว้ก่อนหน้านี้จะถูก<strong>ปรับเปลี่ยนเป็น "ตามที่นับได้" ทั้งหมด</strong></li>
                            <li>ระบบจะบันทึกข้อมูลลงฐานข้อมูลทันทีหลังจากยืนยัน</li>
                        </ul>
                        <p style="color: #d9534f; margin-top: 15px;">
                            <strong>คุณแน่ใจหรือไม่ที่จะดำเนินการต่อ?</strong>
                        </p>
                    </div>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#f0ad4e',
                cancelButtonColor: '#6c757d',
                confirmButtonText: '<i class="fa fa-check"></i> ยืนยัน',
                cancelButtonText: '<i class="fa fa-times"></i> ยกเลิก',
                customClass: {
                    popup: 'swal-wide'
                }
            });

            // ถ้าผู้ใช้ยกเลิก ให้หยุดการทำงาน
            if (!result.isConfirmed) {
                return;
            }

            try {
                window.loading_on();

                // ตั้งค่าการจัดการเป็น "ตามที่นับได้" (action_id = 3) สำหรับทุกรายการ
                this.stock_count_products.forEach((product) => {
                    product.selected_product_action_id = 3;
                    product.selected_product_action_name = this.stock_count_actions.find(
                        (action) => action.id === 3
                    )?.label;
                    product.action_manual = null;
                    product.is_edit = true;
                });

                this.stock_count_lots.forEach((lot) => {
                    lot.selected_product_action_id = 3;
                    lot.selected_product_action_name = this.stock_count_actions.find(
                        (action) => action.id === 3
                    )?.label;
                    lot.action_manual = null;
                    lot.is_edit = true;
                });

                this.saveManagedData();
                this.$forceUpdate();

                // เตรียมข้อมูลทั้งหมดสำหรับบันทึกครั้งเดียว
                const bulkUpdateData = this.stock_count_products.map((product) => {
                    const lots = this.stock_count_lots.filter(
                        (lot) => lot.product_id === product.product_id
                    );

                    const lotsData = lots.map((lot) => ({
                        manufacturing_no: lot.manufacturing_no,
                        expiry_date: lot.expiry_date,
                        counted_qty: lot.counted_qty || 0,
                        sold_qty_before_count: lot.sold_qty_before_count || 0,
                        sold_qty_after_count: lot.sold_qty_after_count || 0,
                        action: lot.selected_product_action_id,
                        action_value: lot.action_manual || null,
                        note: lot.note || null,
                        is_new: lot.is_new_lot || false,
                    }));

                    return {
                        id: product.id,
                        product_id: product.product_id,
                        product_action: product.selected_product_action_id,
                        product_action_value: product.action_manual || null,
                        lots: lotsData,
                    };
                });

                // บันทึกข้อมูลทั้งหมดครั้งเดียว
                const res = await axios.post('/stock-count/bulk-update-product-discrepancy', {
                    stock_count_id: this.stockCountId,
                    products: bulkUpdateData,
                });

                if (res.data.success) {
                    // อัพเดทข้อมูล timestamp และ updated_by สำหรับแต่ละสินค้า
                    if (res.data.updates) {
                        res.data.updates.forEach((update) => {
                            const product = this.stock_count_products.find(
                                (p) => p.product_id === update.product_id
                            );
                            if (product) {
                                product.latest_manage_at = update.timestamp;
                                product.latest_manage_by = update.updated_by;
                            }
                        });
                    }

                    this.saveManagedData();

                    Swal.fire({
                        title: 'ดำเนินการเรียบร้อย',
                        text: `ตั้งค่าและบันทึกการจัดการเป็น 'ตามที่นับได้' สำหรับสินค้า ${this.stock_count_products.length} รายการแล้ว`,
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                }
            } catch (error) {
                console.error('Error in setAllToCountedQuantity:', error);
                Swal.fire({
                    title: 'เกิดข้อผิดพลาด',
                    text: error.response?.data?.message || 'ไม่สามารถบันทึกข้อมูลได้',
                    icon: 'error',
                });
            } finally {
                window.loading_off();
            }
        },

        exportExcel() {
            window.loading_on();
            axios
                .get(`/stock-count/${this.stockCountId}/export-discrepancies-excel`, {
                    responseType: 'blob',
                })
                .then((response) => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'stock_count.xlsx');
                    document.body.appendChild(link);
                    link.click();
                })
                .catch((error) => {
                    console.error('Error exporting Excel:', error);
                    mySwal.fire({
                        title: 'เกิดข้อผิดพลาด',
                        text: 'ไม่สามารถส่งออกข้อมูลได้',
                        icon: 'error',
                    });
                })
                .finally(() => {
                    window.loading_off();
                });
        },

        getProductQty(product) {
            let sumProduct = product.counted_qty;
            if (product.selected_product_action_id == 1) {
                sumProduct = product.expected_qty;
            } else if (product.selected_product_action_id == 2) {
                sumProduct =
                    product.expected_qty -
                    Number(product.sold_qty_before_count) +
                    Number(product.sold_qty_after_count);
            } else if (product.selected_product_action_id == 3) {
                sumProduct = product.counted_qty;
            } else if (product.selected_product_action_id == 4) {
                sumProduct = product.action_manual;
            } else if (product.selected_product_action_id == 5) {
                sumProduct =
                    product.counted_qty -
                    Number(product.sold_qty_before_count) +
                    Number(product.sold_qty_after_count);
            }
            return sumProduct;
        },

        getSumLotQty(lots) {
            return lots.reduce((sum, item) => {
                console.log(item);

                let amount = item.remaining_amount;
                if (item.selected_product_action_id == 1) {
                    amount = item.expected_qty;
                } else if (item.selected_product_action_id == 2) {
                    amount =
                        item.expected_qty -
                        Number(item.sold_qty_before_count) +
                        Number(item.sold_qty_after_count);
                } else if (item.selected_product_action_id == 3) {
                    amount = item.counted_qty;
                } else if (item.selected_product_action_id == 4) {
                    amount = Number(item.action_manual);
                } else if (item.selected_product_action_id == 5) {
                    amount =
                        item.counted_qty -
                        Number(item.sold_qty_before_count) +
                        Number(item.sold_qty_after_count);
                } else if (item.selected_product_action_id == 6) {
                    amount = item.remaining_amount;
                }

                return Number(amount) + Number(sum);
            }, 0);
        },

        getTargetQtyForCurrentLot() {
            if (!this.currentLotInfo) return 0;

            const lot = this.currentLotInfo;
            if (lot.selected_product_action_id) {
                return this.get_qty_by_action(lot);
            }

            const product = this.stock_count_products.find(
                (p) => p.product_id === lot.product_id
            );
            if (!product) return 0;

            return this.getProductQty(product);
        },

        get_qty_by_action(item) {
            let amount = 0;
            if (item.selected_product_action_id == 1) {
                amount = item.expected_qty;
            } else if (item.selected_product_action_id == 2) {
                amount =
                    Number(item.expected_qty) -
                    (Number(item.sold_qty_before_count) + Number(item.sold_qty_after_count));
            } else if (item.selected_product_action_id == 3) {
                amount = item.counted_qty;
            } else if (item.selected_product_action_id == 4) {
                amount = Number(item.action_manual);
            } else if (item.selected_product_action_id == 5) {
                amount =
                    item.counted_qty -
                    Number(item.sold_qty_before_count) +
                    Number(item.sold_qty_after_count);
            }
            return amount;
        },
    },
};
</script>

<style scoped>
/* Make SweetAlert modal wider for better readability */
::v-deep .swal-wide {
    width: 600px !important;
}

::v-deep .swal-wide ul {
    text-align: left;
    line-height: 1.8;
}

::v-deep .swal-wide li {
    margin-bottom: 8px;
}
</style>
